/**
 * OFT (Omnichain Fungible Token) ABI and utilities for USDT0 bridging.
 *
 * USDT0 uses LayerZero v2 OFT standard. Key distinction:
 * - Ethereum uses an OFT Adapter (locks USDT, mints USDT0 on destination)
 * - All other chains use standard OFT (burns USDT0, mints on destination)
 *
 * The ABI is identical for both — the Adapter exposes the same IOFT interface.
 *
 * References:
 *   - IOFT interface: https://github.com/LayerZero-Labs/LayerZero-v2/blob/main/packages/layerzero-v2/evm/oapp/contracts/oft/interfaces/IOFT.sol
 *   - USDT0 docs: https://docs.usdt0.to/technical-documentation/developer/
 */

/**
 * Minimal IOFT ABI — only the functions we need for bridging.
 */
export const OFT_ABI = [
  // Read the shared decimals (should be 6 for USDT0)
  {
    inputs: [],
    name: 'sharedDecimals',
    outputs: [{ name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
  // Get the underlying token address
  {
    inputs: [],
    name: 'token',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  // Quote the OFT send — returns amount after dust removal
  {
    inputs: [
      {
        components: [
          { name: 'dstEid', type: 'uint32' },
          { name: 'to', type: 'bytes32' },
          { name: 'amountLD', type: 'uint256' },
          { name: 'minAmountLD', type: 'uint256' },
          { name: 'extraOptions', type: 'bytes' },
          { name: 'composeMsg', type: 'bytes' },
          { name: 'oftCmd', type: 'bytes' },
        ],
        name: '_sendParam',
        type: 'tuple',
      },
      { name: '_payInLzToken', type: 'bool' },
    ],
    name: 'quoteOFT',
    outputs: [
      {
        components: [
          { name: 'minAmountLD', type: 'uint256' },
          { name: 'maxAmountLD', type: 'uint256' },
        ],
        name: 'oftLimit',
        type: 'tuple',
      },
      {
        components: [
          { name: 'feeAmountLD', type: 'uint256' },
          { name: 'description', type: 'string' },
        ],
        name: 'oftFeeDetails',
        type: 'tuple[]',
      },
      {
        components: [
          { name: 'amountSentLD', type: 'uint256' },
          { name: 'amountReceivedLD', type: 'uint256' },
        ],
        name: 'oftReceipt',
        type: 'tuple',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  // Quote the messaging fee for a send
  {
    inputs: [
      {
        components: [
          { name: 'dstEid', type: 'uint32' },
          { name: 'to', type: 'bytes32' },
          { name: 'amountLD', type: 'uint256' },
          { name: 'minAmountLD', type: 'uint256' },
          { name: 'extraOptions', type: 'bytes' },
          { name: 'composeMsg', type: 'bytes' },
          { name: 'oftCmd', type: 'bytes' },
        ],
        name: '_sendParam',
        type: 'tuple',
      },
      { name: '_payInLzToken', type: 'bool' },
    ],
    name: 'quoteSend',
    outputs: [
      {
        components: [
          { name: 'nativeFee', type: 'uint256' },
          { name: 'lzTokenFee', type: 'uint256' },
        ],
        name: 'msgFee',
        type: 'tuple',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  // Execute the cross-chain send
  {
    inputs: [
      {
        components: [
          { name: 'dstEid', type: 'uint32' },
          { name: 'to', type: 'bytes32' },
          { name: 'amountLD', type: 'uint256' },
          { name: 'minAmountLD', type: 'uint256' },
          { name: 'extraOptions', type: 'bytes' },
          { name: 'composeMsg', type: 'bytes' },
          { name: 'oftCmd', type: 'bytes' },
        ],
        name: '_sendParam',
        type: 'tuple',
      },
      {
        components: [
          { name: 'nativeFee', type: 'uint256' },
          { name: 'lzTokenFee', type: 'uint256' },
        ],
        name: '_fee',
        type: 'tuple',
      },
      { name: '_refundAddress', type: 'address' },
    ],
    name: 'send',
    outputs: [
      {
        components: [
          { name: 'guid', type: 'bytes32' },
          { name: 'nonce', type: 'uint64' },
          {
            components: [
              { name: 'nativeFee', type: 'uint256' },
              { name: 'lzTokenFee', type: 'uint256' },
            ],
            name: 'fee',
            type: 'tuple',
          },
        ],
        name: 'msgReceipt',
        type: 'tuple',
      },
      {
        components: [
          { name: 'amountSentLD', type: 'uint256' },
          { name: 'amountReceivedLD', type: 'uint256' },
        ],
        name: 'oftReceipt',
        type: 'tuple',
      },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
  // Check enforced options for a pathway
  {
    inputs: [
      { name: '_eid', type: 'uint32' },
      { name: '_msgType', type: 'uint16' },
    ],
    name: 'enforcedOptions',
    outputs: [{ name: '', type: 'bytes' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

/**
 * Standard ERC-20 ABI subset for token approval and balance queries.
 * Note: USDT on Ethereum has non-standard approve (returns void, not bool).
 * viem handles this gracefully when using writeContract.
 */
export const ERC20_ABI = [
  {
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

/**
 * LayerZero Scan API for tracking bridge message status.
 */
export const LZ_SCAN_API_URL =
  'https://api-mainnet.layerzero-scan.com/tx' as const;

/**
 * OFT message type for standard sends (type 1 = SEND).
 */
export const OFT_MSG_TYPE_SEND = 1;

/**
 * Minimum gas for lzReceive on Ink (from enforced options: 80,000).
 * Verified on-chain via enforcedOptions(30101, 1) on Ink OFT.
 */
export const MIN_LZ_RECEIVE_GAS = BigInt("80000");

/**
 * Shared decimals for USDT0 (verified on-chain: sharedDecimals() = 6).
 * Since local decimals are also 6, no dust conversion occurs.
 */
export const USDT0_SHARED_DECIMALS = 6;

/**
 * Gas airdrop configuration for first-time Ink users.
 */
export const GAS_AIRDROP_CONFIG = {
  /** Default airdrop amount in wei (0.0005 ETH) */
  defaultAmount: BigInt("500000000000000"),
  /** Minimum Ink ETH balance below which airdrop is recommended */
  gasThreshold: BigInt("1000000000000000"), // 0.001 ETH
} as const;
